import {
  assignUserRolesBulkResultSchema,
  staffUserListSchema,
  userDetailSchema,
  userListResponseSchema,
  userLookupsSchema,
  type AssignUserRolesBulkRequest,
  type AssignUserRolesRequest,
  type CreateLocalUserRequest,
  type SetLocalPasswordRequest,
  type UpdateUserRequest,
  type UserListQuery,
} from '@gb-ticket/shared-types';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api';
import { toSearchParams } from '@/lib/format';

export const usersQueryKey = ['users'] as const;
export const userLookupsQueryKey = ['users', 'lookups'] as const;

export function userQueryKey(id: string) {
  return ['users', id] as const;
}

export function useUserLookups(options?: { enabled?: boolean; includeInactive?: boolean }) {
  const includeInactive = options?.includeInactive ?? false;
  return useQuery({
    queryKey: [...userLookupsQueryKey, { includeInactive }],
    queryFn: () =>
      apiFetch(
        `/users/lookups${toSearchParams({ includeInactive: includeInactive || undefined })}`,
        userLookupsSchema,
      ),
    enabled: options?.enabled ?? true,
  });
}

export const staffUsersQueryKey = ['users', 'staff'] as const;

/** Active IT staff for team lead and member pickers. */
export function useStaffUsers() {
  return useQuery({
    queryKey: staffUsersQueryKey,
    queryFn: () => apiFetch('/users/staff', staffUserListSchema),
  });
}

export function useUsers(query: UserListQuery) {
  return useQuery({
    queryKey: [...usersQueryKey, query],
    refetchOnWindowFocus: true,
    queryFn: () =>
      apiFetch(
        `/users${toSearchParams({
          page: query.page,
          pageSize: query.pageSize,
          sortBy: query.sortBy,
          sortOrder: query.sortOrder,
          q: query.q,
          role: query.role,
          departmentId: query.departmentId,
          teamId: query.teamId,
          status: query.status === 'all' ? undefined : query.status,
        })}`,
        userListResponseSchema,
      ),
  });
}

export function useUser(id: string | undefined) {
  return useQuery({
    queryKey: id ? userQueryKey(id) : ['users', 'missing'],
    queryFn: () => apiFetch(`/users/${id}`, userDetailSchema),
    enabled: Boolean(id),
  });
}

export function useCreateLocalUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateLocalUserRequest) =>
      apiFetch('/users', userDetailSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: usersQueryKey });
    },
  });
}

export function useUpdateUser(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateUserRequest) =>
      apiFetch(`/users/${id}`, userDetailSchema, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: async (user) => {
      queryClient.setQueryData(userQueryKey(id), user);
      await queryClient.invalidateQueries({ queryKey: usersQueryKey });
    },
  });
}

export function useAssignRolesToMany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: AssignUserRolesBulkRequest) =>
      apiFetch('/users/role-assignments', assignUserRolesBulkResultSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: usersQueryKey });
    },
  });
}

export function useAssignUserRoles(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: AssignUserRolesRequest) =>
      apiFetch(`/users/${id}/roles`, userDetailSchema, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    onSuccess: async (user) => {
      queryClient.setQueryData(userQueryKey(id), user);
      await queryClient.invalidateQueries({ queryKey: usersQueryKey });
    },
  });
}

const passwordLinkSchema = z.object({ ok: z.literal(true) });

export function useSetLocalPassword(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: SetLocalPasswordRequest) =>
      apiFetch(`/users/${id}/local-password`, userDetailSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async (user) => {
      queryClient.setQueryData(userQueryKey(id), user);
      await queryClient.invalidateQueries({ queryKey: usersQueryKey });
    },
  });
}

export function useSendLocalPasswordLink(id: string) {
  return useMutation({
    mutationFn: () =>
      apiFetch(`/users/${id}/local-password/link`, passwordLinkSchema, {
        method: 'POST',
      }),
  });
}
